import { useCallback, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { AppShell, TrackerOnboardingModal } from '@clarix/layout';
import type { ProjectListItem, RailItem } from '@clarix/shared-types';
import { P } from '@clarix/rbac';
import { useProjectsList, useRefreshProjectsOnChange } from '@clarix/services-projects';
import { env } from '@/config/env';
import { useMe } from '@/core/api';
import { useActiveWorkspaceSlug } from '@/gateway/getWorkspaceSlug';
import { APP_REGISTRY } from '@/gateway/apps';
import { AppSwitcher } from './AppSwitcher';
import { useAppOutage } from '@/gateway/appOutageStore';
import { renderSidebarPanel } from '@/gateway/SidebarPanels';
import { useCrmRailItems } from '@/gateway/sidebar/crm-nav';
import { railItemVisible, useAppRailPermissions } from '@/gateway/sidebar/rail-permissions';
import { PANEL_TITLES } from '@/gateway/sidebar/registry';
import { QuickMeetingTopBar } from '@/gateway/quick-meeting';
import {
  readLastProjectsPath,
  writeLastProjectsPath,
} from '@/gateway/sidebar/lastOpenedProject';
import { useAuthStore } from '@clarix/auth-store';
import { useBootTrace } from '@/gateway/devtools/bootTrace';

/** Remembers which app the user was last in, so shell-owned routes that belong
 *  to no app (e.g. /profile) can keep that app's rail instead of an empty one. */
const LAST_APP_KEY = 'clarix.shell.last-app';

/** Rail shown on an app-less route before any app has been visited. Mirrors the
 *  router's DEFAULT_APP_SLUG — the first registered app. */
const DEFAULT_RAIL_APP_SLUG = APP_REGISTRY[0]?.slug ?? 'projects';

/**
 * Shell-level wrapper around <AppShell> from @clarix/layout.
 *
 * Picks the active app from the URL prefix (first segment) and passes
 * its `railItems` down. Admin-only routes are enforced by route-level
 * guards (RequireWorkspaceOwner / RequirePlatformAdmin), so the rail
 * doesn't need to re-check role here.
 *
 * Every app — including admin — renders inside the shared TopBar +
 * SidebarRail chrome. Admin pages render directly into the shell's
 * main slot, same pattern as Projects and PMS (no inner layout).
 */
export function AppShellLayout() {
  const location = useLocation();
  // Same query key + activeSlug as AppSwitcher and WorkspaceShell, so this
  // shares their cached /me rather than issuing another request.
  const meQuery = useMe({ activeSlug: useActiveWorkspaceSlug() ?? undefined });
  // Slug of the app whose backend AppRoute has confirmed is down, or null.
  const outageSlug = useAppOutage();
  // Tracker desktop-app onboarding, opened from the rail's bottom "Tracker"
  // download button (clario-frontend AppShell parity).
  const [trackerModalOpen, setTrackerModalOpen] = useState(false);
  const firstSegment = location.pathname.split('/').filter(Boolean)[0];
  const matchedApp = APP_REGISTRY.find((a) => a.slug === firstSegment);

  // Shell-owned pages (/profile, …) live outside every app, so the URL can't say
  // which rail to show. Without this the sidebar emptied out on those routes and
  // the user lost every way back into the app they came from. Remember the last
  // app and keep ITS rail on app-less routes; sessionStorage carries it across a
  // hard refresh that lands straight on /profile.
  const [lastAppSlug, setLastAppSlug] = useState<string | null>(() =>
    sessionStorage.getItem(LAST_APP_KEY),
  );
  // Guarded adjust-during-render (not an effect) — it settles in one extra pass.
  if (matchedApp && matchedApp.slug !== lastAppSlug) setLastAppSlug(matchedApp.slug);
  useEffect(() => {
    if (lastAppSlug) sessionStorage.setItem(LAST_APP_KEY, lastAppSlug);
  }, [lastAppSlug]);

  // Third fallback for a COLD load of an app-less route (/home from a bookmark
  // or a fresh tab): nothing has stamped lastAppSlug yet, so without this the
  // rail would render empty and leave no way into any app.
  const activeApp =
    matchedApp ??
    APP_REGISTRY.find((a) => a.slug === lastAppSlug) ??
    APP_REGISTRY.find((a) => a.slug === DEFAULT_RAIL_APP_SLUG);
  const isProjectsApp = activeApp?.id === 'projects';
  const rail = useAppRailPermissions(activeApp);
  useBootTrace('AppShellLayout', { app: activeApp?.slug, rail: rail.status });

  // The Projects rail item normally points at the list page, but that page is
  // for admins/PMs (PROJECTS_VIEW_ALL). A member without that permission only
  // has their own projects in the sidebar, so clicking Projects should drop
  // them on their first project rather than an empty/forbidden list. We fetch
  // the list eagerly (only on the Projects app) so the target is ready at
  // click time; the panel and other consumers share this cached query.
  const { data: projectsData } = useProjectsList(undefined, { enabled: isProjectsApp });
  // Project writes happen in other apps' QueryClients; this keeps the sidebar's lists current.
  useRefreshProjectsOnChange();
  const userId = useAuthStore((st) => String(st.user?.sub ?? ''));
  const projectList = projectsData?.data as ProjectListItem[] | undefined;
  const projectIds = new Set(projectList?.map((p) => p.id));
  const railReadyForApp = rail.status === 'ready';
  const canViewAllProjects =
    isProjectsApp && railReadyForApp && rail.permissions[P.PROJECTS_VIEW_ALL] === true;

  // Remember the last Projects-panel page — a project tab, All or Archived —
  // so the rail lands back on it. Only real project ids count: /projects/my-work
  // etc. share the `/projects/:segment` shape.
  const projectsSegment = location.pathname.match(/^\/projects\/([^/]+)/)?.[1];
  const isPanelPage =
    projectsSegment === 'list' ||
    projectsSegment === 'archived' ||
    (!!projectsSegment && projectIds.has(projectsSegment));
  const currentPath = location.pathname + location.search;
  useEffect(() => {
    if (userId && isPanelPage) writeLastProjectsPath(userId, currentPath);
  }, [userId, isPanelPage, currentPath]);

  const lastPath = userId ? readLastProjectsPath(userId) : null;
  const lastSegment = lastPath?.match(/^\/projects\/([^/?]+)/)?.[1];
  const lastProjectPath = lastSegment && projectIds.has(lastSegment) ? lastPath : undefined;
  const lastHeaderPath =
    lastSegment === 'list' || lastSegment === 'archived' ? lastPath : undefined;
  const firstProjectId = projectList?.[0]?.id;

  const resolveRailHref = useCallback(
    (item: RailItem): string | undefined => {
      if (item.id !== 'projects-list') return item.href;
      if (!railReadyForApp) return item.href;
      // admin/PM → back to their last project tab / All / Archived, else the list page
      if (canViewAllProjects) return lastProjectPath ?? lastHeaderPath ?? item.href;
      if (lastProjectPath) return lastProjectPath;
      if (firstProjectId) return `/projects/${firstProjectId}`; // member → first project
      return '/projects'; // no accessible projects → Home (My Work)
    },
    [railReadyForApp, canViewAllProjects, firstProjectId, lastProjectPath, lastHeaderPath],
  );

  // Each app shows ONLY its own rail items (clario-frontend parity: the rail
  // is a single fixed set, no icons borrowed from other apps). Switching apps
  // goes through the AppSwitcher.
  const staticRailItems: RailItem[] = [...(activeApp?.railItems ?? [])];

  // CRM's rail is partly data-driven: entity icons whose object an admin
  // deactivated drop out, and every admin-defined CUSTOM object gains its own
  // icon (→ /crm/o/:apiName). Off the CRM app this is a pass-through and skips
  // the CRM fetches entirely.
  const railItems = useCrmRailItems(staticRailItems, activeApp?.id === 'crm');

  // This app's backend is confirmed down (published by AppRoute after probing
  // /system/status/:app). Its permission endpoint lives on that same api, so the
  // rail map can NEVER resolve while the outage lasts.
  const appIsDown = !!activeApp && outageSlug === activeApp.slug;

  const railLoading = !appIsDown && rail.status === 'loading';

  // Modules this workspace is not entitled to, for the ACTIVE app. A deny-list:
  // an app with no entry, a backend that predates the field, or /me still in
  // flight all yield an empty set and therefore hide nothing — a transitional
  // deploy must never blank someone's rail. The backend denies these routes
  // regardless, so hiding them is a courtesy, not the enforcement.
  const disabledModules = new Set(
    activeApp
      ? (meQuery.data?.active_workspace_apps?.[activeApp.slug.toUpperCase()]
          ?.disabled_modules ?? [])
      : [],
  );

  // During an outage the map will never arrive, and a rail collapsed to its ungated items
  // reads as "your permissions disappeared". Show the full rail instead, rendered inert
  // (railDisabled below) — every in-app route renders the unavailable panel anyway.
  const itemVisible = (it: RailItem) =>
    appIsDown && rail.status !== 'ready'
      ? !(it.moduleCode && disabledModules.has(it.moduleCode))
      : railItemVisible(it, rail, disabledModules);

  const visibleRailItems = railItems.filter(itemVisible);

  // "More" (9-dot) popover items come from the active app only, gated the
  // same way as rail items.
  const visibleMoreItems = (activeApp?.moreItems ?? []).filter(itemVisible);

  const matchesPrefix = (prefixes: readonly string[] | undefined) =>
    (prefixes ?? []).some(
      (prefix) => location.pathname === prefix || location.pathname.startsWith(`${prefix}/`),
    );

  // Immersive full-screen routes (e.g. the support first-run setup wizard): drop
  // ALL chrome — rail AND TopBar — so the flow owns the whole viewport.
  const hideChrome = matchesPrefix(activeApp?.chromeHiddenRoutes);
  // Standalone full-page routes (clario-frontend rendered these without the
  // AppShell): drop the rail so the page owns the full width below the TopBar.
  // chromeHiddenRoutes implies the rail is hidden too.
  const hideRail = hideChrome || matchesPrefix(activeApp?.railHiddenRoutes);

  return (
    <>
      <AppShell
        railItems={visibleRailItems}
        moreItems={visibleMoreItems}
        appSwitcher={<AppSwitcher />}
        renderPanelContent={renderSidebarPanel}
        resolveHref={resolveRailHref}
        // Until this app's map has resolved (or after it failed), gated items are absent — so
        // AppShell must not treat a restored pinned panel as "foreign" and forget it
        // (secondary sidebar closing on refresh).
        railReady={appIsDown || rail.status === 'ready'}
        railLoading={railLoading}
        railDisabled={appIsDown}
        hideRail={hideRail}
        hideTopBar={hideChrome}
        // Panel header titles + rail utility slots come from the active app's
        // sidebar config, so an app names its own panels and picks its own
        // bottom-strip slots instead of the chrome hardcoding both.
        panelTitles={PANEL_TITLES}
        utility={activeApp?.sidebar?.utility}
        appId={activeApp?.id}
        // Tracker desktop-app download — opt-in per app via
        // `sidebar.utility.trackerButton` (only Projects asks for it today).
        onOpenTrackerModal={
          activeApp?.sidebar?.utility?.trackerButton ? () => setTrackerModalOpen(true) : undefined
        }
      />
      {/* Keyed so the timer-slot portal re-finds its target whenever the TopBar remounts. */}
      <QuickMeetingTopBar key={hideChrome ? 'chrome-hidden' : 'chrome'} visible={!hideChrome} />
      <TrackerOnboardingModal
        open={trackerModalOpen}
        onOpenChange={setTrackerModalOpen}
        dmgUrl={env.VITE_TRACKER_DMG_URL}
      />
    </>
  );
}
